import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff, CloudUpload, ArrowBack } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) { setError('Username and password are required'); return; }
    setIsSubmitting(true);
    try {
      await login(username, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Login failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {/* Back link */}
      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBack />}
        sx={{
          position: 'absolute',
          top: 20,
          left: 20,
          color: 'text.secondary',
          '&:hover': { color: 'text.primary' },
        }}
      >
        Back
      </Button>

      <Paper
        elevation={0}
        className="slide-up"
        sx={{
          p: { xs: 3, sm: 5 },
          maxWidth: 420,
          width: '100%',
          borderRadius: 5,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0, height: 2,
            background: 'linear-gradient(90deg, #7C6FFF, #B794FF, #FF6B8A)',
          },
        }}
      >
        <Stack alignItems="center" spacing={1.5} sx={{ mb: 4, mt: 1 }}>
          <Box
            sx={{
              width: 64, height: 64, borderRadius: '20px',
              background: 'linear-gradient(135deg, #7C6FFF, #9D7BFF)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(124,111,255,0.25)',
              animation: 'float 4s ease-in-out infinite',
            }}
          >
            <CloudUpload sx={{ fontSize: 30, color: '#fff' }} />
          </Box>
          <Typography
            variant="h4"
            fontWeight={900}
            sx={{
              background: 'linear-gradient(135deg, #7C6FFF, #FF6B8A)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            UploadMe
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to manage your files
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3, fontSize: '0.85rem' }}>{error}</Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={isSubmitting}
            autoFocus
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            sx={{ mb: 3.5 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isSubmitting}
            sx={{ py: 1.5, fontSize: '0.95rem' }}
          >
            {isSubmitting ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
          </Button>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.8 }}>
              Get credentials from the Telegram bot
              <br />
              Send <strong style={{ color: theme.palette.primary.main }}>/cloud_panel</strong> to get started
            </Typography>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
